export type GnomeAssetPriority = "background" | "foreground";
export type GnomeAssetFileState =
    "missing" | "partial" | "queued" | "downloading" | "bytes_ready";

export interface GnomeAssetLease<T> {
    ready: Promise<T>;
    promote(): void;
    release(): void;
}

interface Resource {
    url: string;
    state: Exclude<GnomeAssetFileState, "missing">;
    consumers: Map<symbol, GnomeAssetPriority>;
    controller: AbortController;
    ready: Promise<Blob>;
    resolve(value: Blob): void;
    reject(error: unknown): void;
    queuedAt: number;
    startedAt: number | null;
    completedAt: number | null;
    bodyBytes: number;
}

export function gnomeAssetAbortError(): DOMException {
    return new DOMException("Gnome asset consumer released", "AbortError");
}

/** Shared byte cache. Priority controls our queue; browser fetch priority is a hint. */
export function createGnomeSpineAssetTransport(
    fetchFile: (url: string, init: RequestInit) => Promise<Blob> = async (
        url,
        init,
    ) => {
        const response = await fetch(url, init);
        if (!response.ok)
            throw new Error(`Gnome asset HTTP ${response.status}`);
        return response.blob();
    },
    now: () => number = () => performance.now(),
) {
    const resources = new Map<string, Resource>();
    let activeForeground = 0;
    let activeBackground = 0;
    let backgroundEnabled = false;
    let pumpQueued = false;

    const priority = (entry: Resource): GnomeAssetPriority =>
        [...entry.consumers.values()].includes("foreground")
            ? "foreground"
            : "background";

    function schedule(): void {
        if (pumpQueued) return;
        pumpQueued = true;
        queueMicrotask(() => {
            pumpQueued = false;
            pump();
        });
    }

    function pump(): void {
        const queued = [...resources.values()].filter(
            (entry) => entry.state === "queued" && entry.consumers.size > 0,
        );
        queued.sort(
            (a, b) =>
                Number(priority(b) === "foreground") -
                Number(priority(a) === "foreground"),
        );
        for (const entry of queued) {
            const lane = priority(entry);
            if (
                lane === "background" &&
                (!backgroundEnabled ||
                    activeBackground >= 1 ||
                    activeForeground > 0)
            )
                continue;
            if (lane === "foreground" && activeForeground >= 4) continue;
            if (lane === "background") activeBackground++;
            else activeForeground++;
            entry.state = "downloading";
            entry.startedAt = now();
            void Promise.resolve()
                .then(() =>
                    fetchFile(entry.url, {
                        signal: entry.controller.signal,
                        priority: lane === "background" ? "low" : "auto",
                    }),
                )
                .then((blob) => {
                    if (entry.controller.signal.aborted)
                        throw gnomeAssetAbortError();
                    entry.state = "bytes_ready";
                    entry.completedAt = now();
                    entry.bodyBytes = blob.size;
                    entry.resolve(blob);
                })
                .catch((error: unknown) => {
                    if (resources.get(entry.url) === entry)
                        resources.delete(entry.url);
                    entry.reject(error);
                })
                .finally(() => {
                    if (lane === "background") activeBackground--;
                    else activeForeground--;
                    schedule();
                });
        }
    }

    function acquire(
        url: string,
        initialPriority: GnomeAssetPriority,
    ): GnomeAssetLease<Blob> {
        let entry = resources.get(url);
        if (!entry) {
            let resolve!: (value: Blob) => void;
            let reject!: (error: unknown) => void;
            const ready = new Promise<Blob>((accept, fail) => {
                resolve = accept;
                reject = fail;
            });
            // Consumers may release before attaching their own await/catch.
            void ready.catch(() => undefined);
            entry = {
                url,
                state: "queued",
                consumers: new Map(),
                controller: new AbortController(),
                ready,
                resolve,
                reject,
                queuedAt: now(),
                startedAt: null,
                completedAt: null,
                bodyBytes: 0,
            };
            resources.set(url, entry);
        }
        const resource = entry;
        const consumer = Symbol();
        resource.consumers.set(consumer, initialPriority);
        schedule();
        return {
            ready: resource.ready,
            promote() {
                if (!resource.consumers.has(consumer)) return;
                resource.consumers.set(consumer, "foreground");
                schedule();
            },
            release() {
                resource.consumers.delete(consumer);
                // A StrictMode remount in the same turn can reacquire the request.
                queueMicrotask(() => {
                    if (
                        resource.consumers.size ||
                        resource.state === "bytes_ready"
                    )
                        return;
                    if (resources.get(url) === resource) resources.delete(url);
                    resource.controller.abort();
                    resource.reject(gnomeAssetAbortError());
                    schedule();
                });
            },
        };
    }

    return {
        acquire,
        setBackgroundEnabled(enabled: boolean) {
            backgroundEnabled = enabled;
            schedule();
        },
        state(urls: readonly string[]): GnomeAssetFileState {
            const entries = urls.map((url) => resources.get(url));
            if (entries.every((entry) => entry?.state === "bytes_ready"))
                return "bytes_ready";
            if (entries.some((entry) => entry?.state === "downloading"))
                return "downloading";
            if (entries.some((entry) => entry?.state === "queued"))
                return "queued";
            if (entries.some((entry) => entry !== undefined)) return "partial";
            return "missing";
        },
        diagnostics(urls: readonly string[]) {
            const entries = urls.flatMap((url) => resources.get(url) ?? []);
            return {
                file_count: urls.length,
                completed_file_count: entries.filter(
                    (entry) => entry.state === "bytes_ready",
                ).length,
                body_bytes: entries.reduce(
                    (total, entry) => total + entry.bodyBytes,
                    0,
                ),
                queue_ms: entries.reduce(
                    (total, entry) =>
                        total +
                        (entry.startedAt === null
                            ? 0
                            : entry.startedAt - entry.queuedAt),
                    0,
                ),
                fetch_body_ms: entries.reduce(
                    (total, entry) =>
                        total +
                        (entry.completedAt === null || entry.startedAt === null
                            ? 0
                            : entry.completedAt - entry.startedAt),
                    0,
                ),
            };
        },
    };
}
